// (C) Copyright 2025 Hewlett Packard Enterprise Development LP

import { Box, Button } from 'grommet'
import React from 'react'
import { FormNext } from 'grommet-icons'
import PropTypes from 'prop-types'

import { Typography, Loader } from '../../../components'

import {
  MarkdownComponent,
  ListComponent,
  AdditionalResults,
  SimilarMatches,
  ActionResults,
  FooterComponent
} from './components'

export const GlobalSearchPanel = ({
  content,
  handleListClick,
  handleActionClick,
  isAutoCompletion,
  showRecentSearch,
  loading,
  showFaq,
  setOpenDropdown,
  i18nTranslation
}) => {
  const { historyObject = {} } = document.querySelector('greenlake-header')
  return (
    <Box
      elevation="large"
      pad={{ horizontal: 'medium', vertical: 'small' }}
      width="large"
      height={{ min: 'auto', max: 'large' }}
      round="small"
      style={{ position: 'absolute', zIndex: 100 }}
      background="background"
      overflow="auto"
      direction="column"
      margin={{ horizontal: 'small' }}
      data-testid="global-search-drop-down"
    >
      {loading ? (
        <Box align="center" justify="center" margin="large">
          <Loader testId="global-search-loader" />
        </Box>
      ) : (
        <Box direction="column" flex="grow">
          {(isAutoCompletion || showFaq) && content?.items && (
            <Box
              pad={{ bottom: 'small' }}
              border={{
                side: 'bottom',
                color: 'border-weak',
                size: 'small'
              }}
            >
              {showFaq && (
                <Typography
                  size="xsmall"
                  type="text"
                  testId="faq-heading"
                  color="text-weak"
                  margin={{ vertical: 'xsmall', horizontal: 'xsmall' }}
                >
                  {i18nTranslation('global_search.faq')}
                </Typography>
              )}
              <ListComponent
                content={content}
                handleListClick={handleListClick}
                handleActionClick={handleActionClick}
              />
            </Box>
          )}
          {showRecentSearch && (
            <>
              {content?.searchFailed ? (
                <Box direction="column">
                  <Box height="auto">
                    <MarkdownComponent data={content?.searchFailed} />
                  </Box>
                </Box>
              ) : (
                <>
                  {content?.summary && content?.actual_match && (
                    <Box
                      direction="column"
                      pad={{ bottom: 'medium', top: 'small' }}
                      border={{
                        side: 'bottom',
                        color: 'border-weak',
                        size: 'small'
                      }}
                    >
                      <Typography
                        type="text"
                        testId="summary-title"
                        color="text-weak"
                        size="xsmall"
                      >
                        {i18nTranslation('global_search.summary')}
                      </Typography>
                      <Box height="auto">
                        <MarkdownComponent data={content?.summary} />
                        {content?.doc_results && (
                          <Button
                            data-testid="read-button"
                            pad={{ vertical: 'none', horizontal: 'small' }}
                            href={content?.doc_results[0].link}
                            target="_blank"
                            label={
                              <Typography
                                type="text"
                                size="medium"
                                weight="bold"
                                emphasis
                              >
                                {i18nTranslation('global_search.read_more')}
                              </Typography>
                            }
                            type="default"
                            icon={<FormNext size="large" />}
                            alignSelf="start"
                            reverse
                            align="baseline"
                            onClick={() => handleActionClick()}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                  <Box direction="column" gap="small">
                    {content?.action && (
                      <ActionResults
                        data={content?.action}
                        historyObject={historyObject}
                        setOpenDropdown={setOpenDropdown}
                        i18nTranslation={i18nTranslation}
                        handleActionClick={handleActionClick}
                      />
                    )}
                    {content?.results && (
                      <AdditionalResults
                        data={content?.results}
                        similarMatch={false}
                        i18nTranslation={i18nTranslation}
                        handleActionClick={handleActionClick}
                      />
                    )}
                    {content?.similar_match && (
                      <SimilarMatches
                        data={content?.similar_matches}
                        handleListClick={handleListClick}
                        i18nTranslation={i18nTranslation}
                      />
                    )}
                  </Box>
                </>
              )}
            </>
          )}
          <FooterComponent i18nTranslation={i18nTranslation} />
        </Box>
      )}
    </Box>
  )
}
GlobalSearchPanel.propTypes = {
  content: PropTypes.any.isRequired,
  handleListClick: PropTypes.func.isRequired,
  handleActionClick: PropTypes.func.isRequired,
  isAutoCompletion: PropTypes.bool.isRequired,
  showRecentSearch: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  showFaq: PropTypes.bool.isRequired,
  setOpenDropdown: PropTypes.func.isRequired,
  i18nTranslation: PropTypes.any.isRequired
}
